import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { StackScreenProps } from "@react-navigation/stack";
import { useMutation, useQuery } from "@apollo/client/react";
import {
    GET_CONTRACT,
    GET_MISSION_WITH_TERMINATION,
    REGENERATE_CONTRACT_ASSETS,
} from "../../graphql/core";
import ScreenCard, { ButtonType } from "../ScreenCard";
import { useI18n } from "../../hooks/i18n-hooks";
import { ContractSignatureStatus, SimpleSigningState } from "../../__generated__/graphql";
import { GetAmendmentQuery, GetContractQuery } from "../../__generated__/operations/graphql";
import VStack from "../../ui_atoms/VStack";
import apolloClient from "../../configs/graphql.config";
import { BodyMediumText, TitleMediumText } from "../../components/FormattedText";
import { useIntercom } from "../../hooks/intercom";
import { useDisableBackButton } from "../../hooks/use-disable-back-button";
import { BubbleTalk, BubbleTalkPosition } from "../../components/BubbleTalk";
import DocuIllSvg from "../../assets/images/docu_ill.svg";
import Avatar1Svg from "../../assets/images/avatar_1.svg";
import Avatar2Svg from "../../assets/images/avatar_2.svg";
import { kycGradient } from "../../configs/linear-gradients";
import { useTheme } from "../../hooks/use-theme";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import Loading from "../../components/Loading";
import { View } from "react-native";
import { useSnackbar } from "../../hooks/snackbar";
import { usePreventGoingBack } from "../../hooks/use-prevent-going-back";
import { GET_AMENDMENT, GET_MISSION_AMENDMENTS } from "../../graphql/amendment";
import { XOR } from "ts-essentials";
import { ContractType } from "../../types";

type PreSignParamList = {
    CreateContractPreSign: { contractId: string; contractType?: ContractType };
    ContractPreviewScreen: { contractId: string; contractType?: ContractType };
    Home: undefined;
};

type PreSignScreenProps = StackScreenProps<PreSignParamList, "CreateContractPreSign">;

/**
 * This screen is used to check if the contract is ready to be signed
 * [ButtonsConfig, labelText, infoText]
 */
const ERRONEOUS_VALUE: [ButtonsConfig, BubbleTalkProps?, string?] = [
    { canClose: true },
    undefined,
    "screens.pre_sign.invalid_info",
];

type BubbleTalkProps = {
    message: string;
    image: React.ComponentType;
    position: BubbleTalkPosition;
};

type ButtonsConfig = {
    canSign?: boolean;
    canClose?: boolean;
};

const STATUS_BUTTON_MAP: Record<
    ContractSignatureStatus | "none",
    [ButtonsConfig?, BubbleTalkProps?, string?]
> = {
    none: [],
    canceled_by_lse: ERRONEOUS_VALUE,
    canceled_by_other: ERRONEOUS_VALUE,
    canceled_by_self: ERRONEOUS_VALUE,
    failed: ERRONEOUS_VALUE,
    need_other_sign: ERRONEOUS_VALUE,
    need_seal: ERRONEOUS_VALUE,
    handwritten_in_process: [{ canClose: true }],
    success: ERRONEOUS_VALUE,
    need_self_sign: [
        { canSign: true, canClose: true },
        { message: "screens.pre_sign.label_ready", image: Avatar1Svg, position: "bottom-right" },
        "screens.pre_sign.valid_info",
    ],
    waiting_self_sign: [
        { canSign: true, canClose: true },
        { message: "screens.pre_sign.label_ready", image: Avatar1Svg, position: "bottom-right" },
        "screens.pre_sign.valid_info",
    ],
    waiting_contract: [
        undefined,
        { message: "screens.pre_sign.label_waiting", image: DocuIllSvg, position: "top-left" },
        "screens.pre_sign.valid_info",
    ],
};

const CreateContractPreSign = ({ route, navigation }: PreSignScreenProps) => {
    const timeoutRef = useRef<NodeJS.Timeout | null>(null); // handle timeout for asset generation
    const { presentMessageComposer } = useIntercom();
    const { colors, marginSize } = useTheme();
    const insets = useSafeAreaInsets();

    const { contractId, contractType = "contract" } = route.params;

    const i18n = useI18n();
    const [isPolling, setIsPolling] = useState(true);
    const [hasAlreadyTimeout, setHasAlreadyTimeout] = useState(false);
    const { showSnackbar } = useSnackbar();
    const { data, error, loading } = useQuery<XOR<GetContractQuery, GetAmendmentQuery>>(
        contractType === "contract" ? GET_CONTRACT : GET_AMENDMENT,
        {
            variables: { id: contractId },
            pollInterval: !isPolling ? 0 : 5000, // 5 seconds, waiting for the contract to become ready
        },
    );

    const contractOrAmendment = data?.getContract || data?.getAmendment;

    const signatureStatus = contractOrAmendment?.signatureStatus;

    // Handwritten clients sign on paper: the contract is sent by post, signed by hand
    // and imported back by Lift, so the screen only shows that information.
    const isHandwritten =
        contractOrAmendment?.signingState === SimpleSigningState.WaitingHandwritten;

    const [doRegenerateContracts] = useMutation(REGENERATE_CONTRACT_ASSETS, {
        variables: { contractId },
    });

    const onTimeout = useCallback(() => {
        setIsPolling(false);
        setHasAlreadyTimeout(true);
    }, []);

    const stopTimeout = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    /**
     * Timeout is started when the screen is mounted and assets regeneration is requested
     * This will handle problem during doppio ( html -> PDF ) asset generation, we will
     * be able to re-generate the assets...
     */
    const startTimeout = useCallback(() => {
        stopTimeout();
        setHasAlreadyTimeout(false);
        timeoutRef.current = setTimeout(
            () => {
                onTimeout();
            },
            1000 * 60 * 2,
        ); // 2 minutes
    }, [onTimeout, stopTimeout]);

    const onRegenerate = useCallback(() => {
        const _func = async () => {
            stopTimeout();
            setIsPolling(true);
            try {
                await doRegenerateContracts();
            } catch (_e) {
                showSnackbar({
                    mode: "error",
                    text: i18n.t("screens.pre_sign.error"),
                });
            } finally {
                setIsPolling(true);
                startTimeout();
            }
        };
        void _func();
    }, [stopTimeout, doRegenerateContracts, showSnackbar, i18n, startTimeout]);

    const onContactSupport = useCallback(() => {
        void presentMessageComposer();
        navigation.reset({
            index: 0,
            routes: [{ name: "Home" }],
        });
    }, [presentMessageComposer, navigation]);
    /**
     * react on signature status change
     */
    useEffect(() => {
        setIsPolling(() => {
            if (!data) {
                return true;
            }

            const newIsPolling = signatureStatus === ContractSignatureStatus.WaitingContract;
            if (!newIsPolling) {
                // stop timeout, the contract is ready or is in error
                stopTimeout();
                void apolloClient.refetchQueries({
                    include: [GET_MISSION_WITH_TERMINATION, GET_MISSION_AMENDMENTS],
                });
            }
            return newIsPolling;
        });
    }, [signatureStatus, data, stopTimeout]);

    /**
     * Handle component mount and unmount
     */
    useEffect(() => {
        startTimeout();
        return () => {
            stopTimeout();
        };
    }, [startTimeout, stopTimeout]);

    const [buttonsConfig, bubbleTalkData, infoText] = STATUS_BUTTON_MAP[signatureStatus || "none"];

    const signNow = useCallback(() => {
        navigation.navigate("ContractPreviewScreen", {
            contractId,
            contractType,
        });
    }, [contractId, navigation, contractType]);

    const close = useCallback(() => {
        navigation.reset({
            index: 0,
            routes: [{ name: "Home" }],
        });
    }, [navigation]);

    // disable hardware back button
    useDisableBackButton();
    // prevent going back before timeout
    usePreventGoingBack({ prevent: isPolling, warning: false });

    useEffect(() => {
        if (buttonsConfig?.canSign) {
            // When ready to sign, navigate to the contract preview screen
            navigation.popTo("ContractPreviewScreen", {
                contractId: route.params.contractId,
                contractType,
            });
        }
    }, [buttonsConfig?.canSign, navigation, route.params.contractId, contractType]);

    const buttons = useMemo<ButtonType[]>(() => {
        return !hasAlreadyTimeout
            ? [
                  ...(buttonsConfig?.canSign
                      ? [
                            {
                                title: i18n.t("screens.pre_sign.read_and_sign_contract"),
                                mode: "secondary" as const,
                                validating: loading,
                                onPress: signNow,
                            },
                        ]
                      : []),
                  ...(buttonsConfig?.canClose
                      ? [
                            {
                                title: i18n.t("close"),
                                mode: "outlined" as const,
                                onPress: close,
                            },
                        ]
                      : []),
              ]
            : [
                  {
                      title: i18n.t("screens.pre_sign.timeout_retry"),
                      mode: "outlined" as const,
                      onPress: onRegenerate,
                  },
                  {
                      title: i18n.t("screens.pre_sign.timeout_contact"),
                      mode: "secondary" as const,
                      onPress: onContactSupport,
                  },
              ];
    }, [
        buttonsConfig?.canClose,
        buttonsConfig?.canSign,
        close,
        hasAlreadyTimeout,
        i18n,
        loading,
        onContactSupport,
        onRegenerate,
        signNow,
    ]);
    if (isHandwritten) {
        return (
            <ScreenCard
                buttons={[
                    {
                        title: i18n.t("close"),
                        mode: "outlined",
                        onPress: close,
                    },
                ]}
                style={{ paddingTop: insets.top }}
                isContentContainerFlex
                backgroundGradient={kycGradient}
            >
                <VStack h="2/3" v={1} style={{ flex: 1 }}>
                    <BubbleTalk
                        personImageComponent={<Avatar2Svg width="100%" height="100%" />}
                        position="top-center"
                        bubbleBackgroundColor={colors.background}
                    >
                        <TitleMediumText>
                            {i18n.t("screens.pre_sign.handwritten_title")}
                        </TitleMediumText>
                    </BubbleTalk>
                    <View style={{ flex: 1, justifyContent: "space-evenly" }}>
                        <BodyMediumText style={{ textAlign: "center", marginTop: marginSize }}>
                            {i18n.t("screens.pre_sign.handwritten_info")}
                        </BodyMediumText>
                    </View>
                </VStack>
            </ScreenCard>
        );
    }

    return (
        <ScreenCard
            buttons={buttons}
            headband={
                error || hasAlreadyTimeout
                    ? {
                          text: hasAlreadyTimeout
                              ? i18n.t("screens.pre_sign.timeout")
                              : i18n.t("screens.pre_sign.error"),
                          mode: "error",
                      }
                    : undefined
            }
            style={{ paddingTop: insets.top }}
            isContentContainerFlex
            backgroundGradient={kycGradient}
        >
            <VStack h="2/3" v={1} style={{ flex: 1 }}>
                {bubbleTalkData && (
                    <BubbleTalk
                        personImageComponent={<bubbleTalkData.image />}
                        position={bubbleTalkData.position}
                        bubbleBackgroundColor={colors.background}
                    >
                        <TitleMediumText>
                            {i18n.t(bubbleTalkData.message, {
                                contractType: i18n.t(`screens.pre_sign.${contractType}`),
                            })}
                        </TitleMediumText>
                    </BubbleTalk>
                )}
                <View style={{ flex: 1, justifyContent: "space-evenly" }}>
                    {signatureStatus === ContractSignatureStatus.WaitingContract && <Loading />}
                    {infoText && (
                        <BodyMediumText style={{ textAlign: "center", marginTop: marginSize }}>
                            {i18n.t(infoText)}
                        </BodyMediumText>
                    )}
                </View>
            </VStack>
        </ScreenCard>
    );
};

export default CreateContractPreSign;
